<template>
	<section class="chart-card">
		<header class="chart-head">
			<h3>{{ title }}</h3>
			<p v-if="subtitle">{{ subtitle }}</p>
		</header>
		<div v-if="isEmpty" class="chart-empty">{{ emptyText }}</div>
		<div v-else ref="chartEl" class="chart-canvas" :style="{ height }"></div>
	</section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import * as echarts from 'echarts';

const props = defineProps({
	title: {
		type: String,
		required: true,
	},
	subtitle: {
		type: String,
		default: '',
	},
	option: {
		type: Object,
		default: () => ({}),
	},
	height: {
		type: String,
		default: '280px',
	},
	empty: {
		type: Boolean,
		default: false,
	},
	emptyText: {
		type: String,
		default: 'No data for selected range.',
	},
});

const chartEl = ref(null);
let chartInstance = null;
let resizeObserver = null;

const isEmpty = computed(() => Boolean(props.empty));

const renderChart = async () => {
	if (isEmpty.value) return;
	await nextTick();
	if (!chartEl.value) return;
	if (!chartInstance) {
		chartInstance = echarts.init(chartEl.value);
	}
	chartInstance.setOption(props.option || {}, true);
	chartInstance.resize();
};

const disposeChart = () => {
	if (chartInstance) {
		chartInstance.dispose();
		chartInstance = null;
	}
};

onMounted(async () => {
	await renderChart();
	if (chartEl.value) {
		resizeObserver = new ResizeObserver(() => {
			chartInstance?.resize();
		});
		resizeObserver.observe(chartEl.value);
	}
});

watch(
	() => props.option,
	async () => {
		await renderChart();
	},
	{ deep: true },
);

watch(
	() => props.empty,
	async (nextEmpty) => {
		if (nextEmpty) {
			disposeChart();
			return;
		}
		await renderChart();
	},
);

onBeforeUnmount(() => {
	if (resizeObserver) {
		resizeObserver.disconnect();
		resizeObserver = null;
	}
	disposeChart();
});
</script>

<style scoped>
.chart-card {
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 8px;
	background: var(--noc-background, #fff);
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	box-shadow: none;
}

.chart-head h3 {
	margin: 0;
	font: var(--ap-type-section, 600 0.9375rem/1.375rem var(--font-family-base));
	color: var(--text-color, #252a34);
}

.chart-head p {
	margin: 2px 0 0;
	font: var(--ap-type-caption, 400 0.8125rem/1.125rem var(--font-family-base));
	color: var(--text-color-secondary, #5e6170);
}

.chart-empty {
	height: 200px;
	display: flex;
	align-items: center;
	justify-content: center;
	text-align: center;
	color: var(--text-color-secondary, #5e6170);
	font: var(--ap-type-body, 400 0.875rem/1.25rem var(--font-family-base));
	border: 1px dashed var(--border-color, #dcdcdc);
	border-radius: 8px;
}

.chart-canvas {
	width: 100%;
	min-height: 160px;
}
</style>
